import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { ExternalLink } from '@/components/ExternalLink'
import { FormSection, FormSectionConfig } from '@/components/FormSection'

import { licenseSlice } from '@/store/licenseSlice'
import {
  EMAIL_REGEX,
  ITALIAN_FISCAL_CODE_PATTERN,
  ITALIAN_FISCAL_CODE_REGEX,
} from '@/utils/regex.ts'

const validateRequired = (errorMsg: string) => (value: string) => {
  if (!value.trim()) {
    return errorMsg
  }
}

export function UserFormSection() {
  const { t } = useTranslation()

  const emailLabel = t('insertDataForm.user.email')
  const phoneLabel = t('insertDataForm.user.phone')
  const lastNameLabel = t('insertDataForm.user.lastName')
  const firstNameLabel = t('insertDataForm.user.firstName')
  const fiscalCodeLabel = t('insertDataForm.user.fiscalCode')

  const isTaxCodeRequired = useSelector(
    licenseSlice.selectors.isTaxCodeRequired
  )

  const inputs: FormSectionConfig[] = [
    {
      autoComplete: 'given-name',
      className: 'col-span-1',
      label: firstNameLabel,
      name: 'user.firstName',
      placeholder: t('placeholder.firstName'),
      rules: {
        required: t('validation.requiredField_m', {
          fieldName: firstNameLabel.toLowerCase(),
        }),
        validate: validateRequired(
          t('validation.requiredField_m', {
            fieldName: firstNameLabel.toLowerCase(),
          })
        ),
      },
    },
    {
      autoComplete: 'family-name',
      className: 'col-span-1',
      label: lastNameLabel,
      name: 'user.lastName',
      placeholder: t('placeholder.lastName'),
      rules: {
        validate: validateRequired(
          t('validation.requiredField_m', {
            fieldName: lastNameLabel.toLowerCase(),
          })
        ),
      },
    },
    {
      className: 'col-span-2 lg:col-span-1',
      label: phoneLabel,
      name: 'user.phoneNumber',
      prefixName: 'user.phonePrefix',
      type: 'tel',
    },
    {
      autoComplete: 'email',
      className: 'col-span-2 lg:col-span-1',
      inputType: 'email',
      label: emailLabel,
      name: 'user.email',
      placeholder: t('placeholder.email'),
      rules: {
        pattern: { message: t('validation.invalidEmail'), value: EMAIL_REGEX },
        validate: validateRequired(
          t('validation.requiredField_truncated_f', {
            fieldName: emailLabel.toLowerCase(),
          })
        ),
      },
    },
    ...(isTaxCodeRequired
      ? [
          {
            autoComplete: 'off',
            className: 'col-span-2',
            helperText: (
              <Trans i18nKey="insertDataForm.user.fiscalCodeHelpText">
                <ExternalLink
                  aria-label={t('a11y.calculateFiscalCode')}
                  href="https://www.codicefiscaleonline.com/"
                  isInsideParagraph
                  rel="noreferrer noopener nofollow"
                  target="_blank"
                  title={t('a11y.calculateFiscalCode')}
                />
              </Trans>
            ),
            label: fiscalCodeLabel,
            name: 'invoice.fiscalCode',
            pattern: ITALIAN_FISCAL_CODE_PATTERN,
            placeholder: t('placeholder.fiscalCode'),
            rules: {
              maxLength: {
                message: t('validation.invalidFiscalCode'),
                value: 16,
              },
              minLength: {
                message: t('validation.invalidFiscalCode'),
                value: 16,
              },
              pattern: {
                message: t('validation.invalidFiscalCode'),
                value: ITALIAN_FISCAL_CODE_REGEX,
              },
              required: t('validation.requiredField_m', {
                fieldName: fiscalCodeLabel.toLowerCase(),
              }),
            },
          },
        ]
      : []),
  ]

  return <FormSection inputs={inputs} title={t('insertDataForm.user.title')} />
}
